<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { VueFinalModal, useModal } from 'vue-final-modal';
import { RoleEnum } from '@/entities/User';
import { UiModalHeader, ColoredButton } from '@/shared/ui';
import PasswordDisplayModal from './PasswordDisplayModal.vue';
import UserFormContent from './UserFormContent.vue';
import type { PositionItem, FunctionalActivityItem } from './UserFormContent.vue';
import {
  useDepartmentOptions,
  useFunctionalActivityOptions,
  useUserManagement,
  useUserFormValidation,
  resolveUserDismissalState,
  usePositionOptionsCache,
} from '../lib';

export interface CreateUserFormData {
  name: string;
  registrationNumber: string;
  username: string;
  email: string;
  positions: { departmentId: number; positionId: number }[];
  functionalActivityIds: number[];
  admissionDate: Date | null;
  dismissalDate: Date | null;
  status: string;
  role: RoleEnum.USER | RoleEnum.SUPERUSER;
}

defineProps<{
  title?: string;
  showSuperadminToggle?: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save', data: CreateUserFormData): void;
}>();

const name = ref('');
const registrationNumber = ref('');
const username = ref('');
const email = ref('');
const positions = ref<PositionItem[]>([{ departmentId: '', positionId: '' }]);
const functionalActivities = ref<FunctionalActivityItem[]>([{ functionalActivityId: '' }]);
const admissionDate = ref<Date | null>(null);
const dismissalDate = ref<Date | null>(null);
const status = ref('ACTIVE');
const isSuperadmin = ref(false);

const { isLoadingPositions, loadPositionsForDepartment, getPositionOptions } = usePositionOptionsCache();

const { createUser, isCreating } = useUserManagement();

const generatedPassword = ref('');
const lastFormData = ref<CreateUserFormData | null>(null);

const passwordModal = useModal({
  component: PasswordDisplayModal,
  attrs: {
    get password() { return generatedPassword.value; },
    onClose() {
      passwordModal.close();
      if (lastFormData.value) {
        emit('save', lastFormData.value);
      }
    },
  },
});

const {
  departmentOptions,
  isLoading: isLoadingDepartments,
  loadDepartments,
} = useDepartmentOptions();

const {
  functionalActivityOptions,
  isLoading: isLoadingActivities,
  loadFunctionalActivities,
} = useFunctionalActivityOptions();

const {
  serverEmailError,
  serverRegistrationNumberError,
  serverUsernameError,
  emailError,
  admissionDateError,
  dismissalDateError,
  usernameError,
  isFormValid,
  extractFieldErrorMessage,
} = useUserFormValidation({
  name,
  username,
  email,
  registrationNumber,
  admissionDate,
  dismissalDate,
  status,
});

const transformedDepartmentOptions = computed(() => {
  return departmentOptions.value.map(opt => ({
    id: opt.value,
    name: opt.label,
  }));
});

const showPositionSection = false;

onMounted(async () => {
  await Promise.all([loadDepartments(), loadFunctionalActivities()]);
});

const handleClose = () => {
  emit('close');
};

const handleSave = async () => {
  if (!isFormValid.value || isCreating.value) return;

  const validPositions = positions.value
    .filter(p => p.departmentId !== '' && p.positionId !== '')
    .map(p => ({
      departmentId: Number(p.departmentId),
      positionId: Number(p.positionId),
    }));

  const validActivityIds = functionalActivities.value
    .filter(a => a.functionalActivityId !== '')
    .map(a => Number(a.functionalActivityId));
  const resolvedDismissalState = resolveUserDismissalState(
    admissionDate.value,
    dismissalDate.value,
    status.value
  );

  const formData: CreateUserFormData = {
    name: name.value.trim(),
    registrationNumber: registrationNumber.value.trim().padStart(6, '0'),
    username: username.value.trim(),
    email: email.value.trim(),
    positions: validPositions,
    functionalActivityIds: validActivityIds,
    admissionDate: admissionDate.value,
    dismissalDate: resolvedDismissalState.dismissalDate,
    status: resolvedDismissalState.status,
    role: isSuperadmin.value ? RoleEnum.SUPERUSER : RoleEnum.USER,
  };

  lastFormData.value = formData;
  const result = await createUser(formData);

  if (result.fieldErrors && result.fieldErrors.length > 0) {
    const usernameErr = extractFieldErrorMessage(result.fieldErrors, 'username');
    if (usernameErr) {
      serverUsernameError.value = usernameErr;
    }

    const emailErr = extractFieldErrorMessage(result.fieldErrors, 'email');
    if (emailErr) {
      serverEmailError.value = emailErr;
    }

    const registrationNumberErr = extractFieldErrorMessage(result.fieldErrors, 'registrationNumber');
    if (registrationNumberErr) {
      serverRegistrationNumberError.value = registrationNumberErr;
    }

    return;
  }

  if (result.success && result.generatedPassword) {
    generatedPassword.value = result.generatedPassword;
    passwordModal.open();
  }
};

const resetForm = () => {
  name.value = '';
  registrationNumber.value = '';
  username.value = '';
  email.value = '';
  positions.value = [{ departmentId: '', positionId: '' }];
  functionalActivities.value = [{ functionalActivityId: '' }];
  admissionDate.value = null;
  dismissalDate.value = null;
  status.value = 'ACTIVE';
  isSuperadmin.value = false;
  serverUsernameError.value = '';
  serverEmailError.value = '';
  serverRegistrationNumberError.value = '';
  generatedPassword.value = '';
  lastFormData.value = null;
};

const handleClosed = () => {
  resetForm();
};
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[1000px] max-h-[90vh] shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @closed="handleClosed"
  >
    <div class="overflow-hidden flex flex-col max-h-[90vh]">
      <UiModalHeader
        class="px-6 py-4 text-sm border-b border-gray-02 flex-shrink-0"
        :title="title || 'Novo Usuário'"
        @close="handleClose"
      />

      <div class="overflow-y-auto flex-1">
        <UserFormContent
          :name="name"
          :registration-number="registrationNumber"
          :username="username"
          :email="email"
          :positions="positions"
          :functional-activities="functionalActivities"
          :admission-date="admissionDate"
          :dismissal-date="dismissalDate"
          :status="status"
          :is-superadmin="isSuperadmin"
          :show-superadmin-toggle="showSuperadminToggle ?? false"
          :department-options="transformedDepartmentOptions"
          :get-position-options="getPositionOptions"
          :functional-activity-options="functionalActivityOptions"
          :is-loading-departments="isLoadingDepartments"
          :is-loading-positions="isLoadingPositions"
          :is-loading-activities="isLoadingActivities"
          :username-error="usernameError"
          :email-error="emailError"
          :admission-date-error="admissionDateError"
          :dismissal-date-error="dismissalDateError"
          :registration-number-error="serverRegistrationNumberError"
          @update:name="name = $event"
          @update:registration-number="registrationNumber = $event"
          @update:username="username = $event"
          @update:email="email = $event"
          @update:positions="positions = $event"
          @update:functional-activities="functionalActivities = $event"
          @update:admission-date="admissionDate = $event"
          @update:dismissal-date="dismissalDate = $event"
          @update:status="status = $event"
          @update:is-superadmin="isSuperadmin = $event"
          @load-positions="loadPositionsForDepartment"
        />
      </div>

      <div class="px-6 py-4 flex justify-end gap-3 border-t border-gray-02 flex-shrink-0">
        <ColoredButton
          text="Cancelar"
          outlined
          color="gray-03"
          :disabled="isCreating"
          @click="handleClose"
        />
        <ColoredButton
          :disabled="!isFormValid || isCreating"
          :text="isCreating ? 'Salvando...' : 'Salvar'"
          @click="handleSave"
        />
      </div>
    </div>
  </VueFinalModal>
</template>

<style scoped>
.vfm__overlay {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
